import { LOCAL_GDPR_CONSENT } from "@/lib/constant";
import { calculatePackageTotal, getServiceCardListingStartingAmount } from "@/lib/logic";
import { googleAdsPurchaseConversion } from "@/lib/analytics/google-ads";
import { ensureAutoTrackingConsent, syncGoogleConsentFromStorage } from "@/lib/analytics/ga4-consent";
import type { BasketAddonItem, BasketItem, CookieConsentData, Service } from "@/types";
import type { Plan } from "@/types/planTypes";
import type { Currency } from "@/lib/utils/currency";
import type { Order, OrderItem } from "@/types/orders";

declare global {
	interface Window {
		gtag?: (...args: unknown[]) => void;
	}
}

export type Ga4CommerceItem = {
	item_id: string;
	item_name: string;
	item_category?: string;
	item_variant?: string;
	price?: number;
	quantity: number;
	/** 1-based position in the list (GA4 `view_item_list` / `select_item`) */
	index?: number;
};

/** True only after the user has stored consent and opted into performance/analytics cookies. */
function hasConsentForAnalytics(): boolean {
	if (typeof window === "undefined") return false;
	if (!process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_ID) return false;
	try {
		const raw = localStorage.getItem(LOCAL_GDPR_CONSENT);
		if (!raw || !raw.startsWith("{")) return false;
		const parsed = JSON.parse(raw) as Partial<CookieConsentData>;
		return parsed.performance === true;
	} catch {
		return false;
	}
}

function sendEvent(
	eventName: string,
	params: Record<string, unknown>,
	onSent?: () => void,
) {
	const finish = () => onSent?.();
	if (!hasConsentForAnalytics()) {
		finish();
		return;
	}
	syncGoogleConsentFromStorage();
	if (typeof window.gtag !== "function") {
		finish();
		return;
	}

	let done = false;
	const complete = () => {
		if (done) return;
		done = true;
		finish();
	};

	window.gtag("event", eventName, {
		...params,
		transport_type: "beacon",
		...(onSent ? { event_callback: complete, event_timeout: 1000 } : {}),
	});

	if (onSent) window.setTimeout(complete, 1200);
}

/** Fire checkout hits, then leave for Stripe only after they are queued. */
export function goToStripeAfterCheckout(
	url: string,
	params: {
		currency: string;
		value: number;
		items: Ga4CommerceItem[];
		payment_type?: string;
	},
) {
	sendEvent("begin_checkout", {
		currency: params.currency,
		value: params.value,
		items: params.items,
	});
	sendEvent(
		"add_payment_info",
		{
			currency: params.currency,
			value: params.value,
			...(params.payment_type ? { payment_type: params.payment_type } : {}),
			items: params.items,
		},
		() => {
			window.location.assign(url);
		},
	);
}

export function ga4Login(method: string) {
	sendEvent("login", { method });
}

export function ga4SignUp(method: string) {
	sendEvent("sign_up", { method });
}

/**
 * Meaningful CTA only (signup, browse, app download, checkout entry).
 * Do not use for decorative clicks, mouse moves, or heatmaps.
 */
export function ga4CtaClick(params: {
	cta_id: string;
	cta_name: string;
	cta_location: string;
	link_url?: string;
}) {
	sendEvent("cta_click", {
		cta_id: params.cta_id,
		cta_name: params.cta_name,
		cta_location: params.cta_location,
		...(params.link_url ? { link_url: params.link_url } : {}),
	});
}

export function ga4Search(searchTerm: string) {
	const search_term = searchTerm.trim();
	if (!search_term) return;
	sendEvent("search", { search_term });
}

export function ga4ViewBlog(params: { id: string; title: string }) {
	sendEvent("view_blog", {
		blog_id: params.id,
		blog_title: params.title,
	});
}

export function ga4GenerateLead(params: { lead_source: string; item_name?: string }) {
	sendEvent("generate_lead", {
		lead_source: params.lead_source,
		...(params.item_name ? { item_name: params.item_name } : {}),
	});
}

export function ga4AddPaymentInfo(params: {
	currency: string;
	value: number;
	payment_type?: string;
	items: Ga4CommerceItem[];
}) {
	sendEvent("add_payment_info", {
		currency: params.currency,
		value: params.value,
		...(params.payment_type ? { payment_type: params.payment_type } : {}),
		items: params.items,
	});
}

export function ga4Subscribe(params: {
	plan_id: string;
	plan_name: string;
	plan_type?: string;
	frequency?: string;
	value?: number;
	currency?: string;
}) {
	sendEvent("subscribe", {
		plan_id: params.plan_id,
		plan_name: params.plan_name,
		...(params.plan_type ? { plan_type: params.plan_type } : {}),
		...(params.frequency ? { frequency: params.frequency } : {}),
		...(params.currency ? { currency: params.currency } : {}),
		...(typeof params.value === "number" ? { value: params.value } : {}),
	});
}

export function ga4SelectHostingAddon(params: {
	plan_id: string;
	plan_name: string;
	billing_cycle?: string;
}) {
	sendEvent("select_hosting_addon", {
		plan_id: params.plan_id,
		plan_name: params.plan_name,
		...(params.billing_cycle ? { billing_cycle: params.billing_cycle } : {}),
	});
}

export function ga4SaveItem(params: {
	content_type: "service" | "blog";
	item_id: string;
	item_name: string;
	saved: boolean;
}) {
	sendEvent("save_item", {
		content_type: params.content_type,
		item_id: params.item_id,
		item_name: params.item_name,
		saved: params.saved ? 1 : 0,
	});
}

export function ga4Share(params: {
	method: string;
	content_type: string;
	item_id?: string;
}) {
	sendEvent("share", {
		method: params.method,
		content_type: params.content_type,
		...(params.item_id ? { item_id: params.item_id } : {}),
	});
}

export function ga4LivePreviewOpen(params: { item_id: string; item_name?: string }) {
	sendEvent("live_preview_open", {
		item_id: params.item_id,
		...(params.item_name ? { item_name: params.item_name } : {}),
	});
}

export function ga4VideoPlay(params: {
	video_provider: "youtube" | "file" | "review";
	content_type?: string;
	item_id?: string;
}) {
	sendEvent("video_play", {
		video_provider: params.video_provider,
		...(params.content_type ? { content_type: params.content_type } : {}),
		...(params.item_id ? { item_id: params.item_id } : {}),
	});
}

export function ga4CurrencySwitch(params: { currency: string; previous_currency?: string }) {
	sendEvent("currency_switch", {
		currency: params.currency,
		...(params.previous_currency ? { previous_currency: params.previous_currency } : {}),
	});
}

/** Fired when a user picks a different option in “Choose your design” on a service page. */
export function ga4SelectDesign(params: {
	item_id: string;
	item_name?: string;
	design_id: string;
	design_number: number;
	design_title?: string;
	previous_design_id?: string;
}) {
	sendEvent("select_design", {
		item_id: params.item_id,
		design_id: params.design_id,
		design_number: params.design_number,
		...(params.item_name ? { item_name: params.item_name } : {}),
		...(params.design_title ? { design_title: params.design_title } : {}),
		...(params.previous_design_id ? { previous_design_id: params.previous_design_id } : {}),
	});
}

export function ga4SelectPromotion(params: {
	promotion_id: string;
	promotion_name?: string;
	creative_name?: string;
}) {
	sendEvent("select_promotion", {
		promotion_id: params.promotion_id,
		...(params.promotion_name ? { promotion_name: params.promotion_name } : {}),
		...(params.creative_name ? { creative_name: params.creative_name } : {}),
	});
}

export function ga4ChatStart(params: { source: string }) {
	sendEvent("chat_start", { source: params.source });
}

export function ga4SupportTicketCreated(params: { ticket_id?: string; category?: string }) {
	sendEvent("support_ticket_created", {
		...(params.ticket_id ? { ticket_id: params.ticket_id } : {}),
		...(params.category ? { ticket_category: params.category } : {}),
	});
}

export function ga4CompanyReviewSubmit(params: { rating?: number; review_type?: string }) {
	sendEvent("company_review_submit", {
		...(typeof params.rating === "number" ? { rating: params.rating } : {}),
		...(params.review_type ? { review_type: params.review_type } : {}),
	});
}

export function ga4PageNotFound(params?: { page_path?: string; page_location?: string }) {
	const page_path =
		params?.page_path ?? (typeof window !== "undefined" ? window.location.pathname : undefined);
	const page_location =
		params?.page_location ?? (typeof window !== "undefined" ? window.location.href : undefined);
	sendEvent("page_not_found", {
		...(page_path ? { page_path } : {}),
		...(page_location ? { page_location } : {}),
	});
}

export function ga4AffiliateClick(params: { affiliate_ref: string }) {
	const affiliate_ref = params.affiliate_ref.trim();
	if (!affiliate_ref) return;
	sendEvent("affiliate_click", { affiliate_ref });
}

export function ga4NotificationOpen(params: { notification_id?: string }) {
	sendEvent("notification_open", {
		message_id: params.notification_id ?? "unknown",
	});
}

export function buildPlanGa4Item(
	plan: Plan,
	currency: Currency,
	frequency: string,
	index?: number,
): Ga4CommerceItem {
	const priceForFrequency = plan.prices.find((p) => p.duration.toLowerCase() === frequency);
	const price = priceForFrequency?.amounts?.[currency] ?? priceForFrequency?.amount ?? 0;
	const item: Ga4CommerceItem = {
		item_id: plan._id,
		item_name: plan.name,
		item_category: plan.planType === "hosting" ? "Hosting plan" : "Plan",
		item_variant: frequency,
		price: Math.round(price * 100) / 100,
		quantity: 1,
	};
	if (index !== undefined) item.index = index;
	return item;
}

export function ga4ViewItem(params: { currency: string; value: number; items: Ga4CommerceItem[] }) {
	sendEvent("view_item", {
		currency: params.currency,
		value: params.value,
		items: params.items,
	});
}

export function ga4AddToCart(params: { currency: string; value: number; items: Ga4CommerceItem[] }) {
	sendEvent("add_to_cart", {
		currency: params.currency,
		value: params.value,
		items: params.items,
	});
}

export function ga4RemoveFromCart(params: { currency: string; value: number; items: Ga4CommerceItem[] }) {
	sendEvent("remove_from_cart", {
		currency: params.currency,
		value: params.value,
		items: params.items,
	});
}

export function ga4ViewCart(params: { currency: string; value: number; items: Ga4CommerceItem[] }) {
	sendEvent("view_cart", {
		currency: params.currency,
		value: params.value,
		items: params.items,
	});
}

export function ga4BeginCheckout(params: { currency: string; value: number; items: Ga4CommerceItem[] }) {
	sendEvent("begin_checkout", {
		currency: params.currency,
		value: params.value,
		items: params.items,
	});
}

export function ga4ViewItemList(params: {
	item_list_id: string;
	item_list_name: string;
	items: Ga4CommerceItem[];
}) {
	sendEvent("view_item_list", {
		item_list_id: params.item_list_id,
		item_list_name: params.item_list_name,
		items: params.items,
	});
}

export function ga4SelectItem(params: {
	item_list_id: string;
	item_list_name: string;
	items: Ga4CommerceItem[];
}) {
	sendEvent("select_item", {
		item_list_id: params.item_list_id,
		item_list_name: params.item_list_name,
		items: params.items,
	});
}

export function ga4Purchase(params: {
	currency: string;
	value: number;
	transaction_id: string;
	items: Ga4CommerceItem[];
}) {
	ensureAutoTrackingConsent();
	sendEvent("purchase", {
		currency: params.currency,
		value: params.value,
		transaction_id: params.transaction_id,
		items: params.items,
	});
	googleAdsPurchaseConversion({
		currency: params.currency,
		value: params.value,
		transaction_id: params.transaction_id,
	});
}

/** GA4 item_id: stable composite for service + package line. */
export function ga4LineItemId(serviceId: string, packageId: string) {
	return `${serviceId}_${packageId}`;
}

/** Basic tier + template licence in the given currency (for list / carousel analytics). */
export function buildListItemFromService(
	service: Service,
	currency: Currency,
	index?: number,
): Ga4CommerceItem {
	const listPrice = getServiceCardListingStartingAmount(service, currency);
	const item: Ga4CommerceItem = {
		item_id: service._id,
		item_name: service.title,
		item_category: "Service",
		price: Math.round(listPrice * 100) / 100,
		quantity: 1,
	};
	if (index !== undefined) {
		item.index = index;
	}
	return item;
}

export function buildCommerceItemFromBasketLine(
	service: Service,
	item: BasketItem,
	currency: Currency,
): { item: Ga4CommerceItem; lineValue: number } | null {
	const pkg = service.packages?.find((p) => p._id === item.packageId);
	if (!pkg) return null;
	const addonEntries = Object.entries(item.addons) as [string, BasketAddonItem][];
	const { packageTotal, addonTotal } = calculatePackageTotal(pkg, service.addons, addonEntries, currency);
	const lineValue = (packageTotal + addonTotal) * Math.max(1, item.quantity);
	const unitPrice = lineValue / Math.max(1, item.quantity);
	return {
		lineValue,
		item: {
			item_id: ga4LineItemId(service._id, item.packageId),
			item_name: service.title,
			item_category: "Service",
			item_variant: pkg.title,
			price: Math.round(unitPrice * 100) / 100,
			quantity: item.quantity,
		},
	};
}

export function buildGa4CartFromBasket(
	items: BasketItem[],
	servicesById: Record<string, Service | undefined>,
	currency: Currency,
): { value: number; items: Ga4CommerceItem[] } {
	let value = 0;
	const gaItems: Ga4CommerceItem[] = [];
	for (const bi of items) {
		const svc = servicesById[bi.serviceId];
		if (!svc) continue;
		const built = buildCommerceItemFromBasketLine(svc, bi, currency);
		if (!built) continue;
		value += built.lineValue;
		gaItems.push(built.item);
	}
	return {
		value: Math.round(value * 100) / 100,
		items: gaItems,
	};
}

function orderItemTitle(it: OrderItem): string {
	const sid = it.serviceId;
	if (sid && typeof sid === "object" && "title" in sid && typeof (sid as { title?: string }).title === "string") {
		return (sid as { title: string }).title;
	}
	return "Service";
}

function orderItemVariant(it: OrderItem): string | undefined {
	const sid = it.serviceId;
	if (!sid || typeof sid !== "object" || !("packages" in sid)) return undefined;
	const pkgs = (sid as Service).packages;
	const p = pkgs?.find((x) => x._id === it.package.packageId);
	return p?.title;
}

export function buildPurchaseFromOrder(order: Order, currency: Currency): { value: number; items: Ga4CommerceItem[] } {
	const rawTotal = order.amountTotal ?? 0;
	const value = Math.round((rawTotal / 100) * 100) / 100;
	const items: Ga4CommerceItem[] = (order.items ?? []).map((it) => ({
		item_id: ga4LineItemId(
			typeof it.serviceId === "object" && it.serviceId && "_id" in it.serviceId
				? String((it.serviceId as { _id: string })._id)
				: "unknown",
			it.package.packageId,
		),
		item_name: orderItemTitle(it),
		item_category: "Service",
		item_variant: orderItemVariant(it),
		price: undefined,
		quantity: it.package.quantity,
	}));
	return { value, items };
}

/** Returns true the first time per tab for this order id (avoids duplicate purchase on refresh). */
/** Align with server Measurement Protocol when `stripeSessionId` is present. */
export function ga4PurchaseTransactionId(order: Order): string {
	const sid = order.stripeSessionId?.trim();
	if (sid) return `${order.id}_${sid}`;
	return order.id;
}

export function consumeGa4PurchaseOnce(orderId: string): boolean {
	if (typeof window === "undefined") return false;
	try {
		const key = `ga4_purchase_sent:${orderId}`;
		if (sessionStorage.getItem(key)) return false;
		sessionStorage.setItem(key, "1");
		return true;
	} catch {
		return false;
	}
}
